import { Box, Button } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { QrCodeVisitStatus, QrCodeVisitWeb } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useMemo } from 'react';
import { dataGridIdSlots } from 'v2/components/DataGrid/dataGridIdSlots';
import { renderHeaderWithId } from 'v2/components/DataGrid/renderHeaderWithId';
import useModalContext from 'v2/components/Modal/useModalContext';
import { TwoLineCell } from 'v2/components/TextCell/TextCell';
import { DATAGRID_DEFAULT_PAGE_SIZE, DATAGRID_PAGE_SIZE_OPTIONS, DATAGRID_ROW_HEIGHT } from 'v2/constants';
import { formatDate } from 'v2/utils/dateTime';
import CancelVisitModal from './CancelVisitModal';

interface Props {
  visits: QrCodeVisitWeb[];
  isLoading: boolean;
  onCancelVisit?: (visitId: string) => Promise<void>;
  showDetails?: boolean;
}

const CANCEL_BUTTON_CLASS = 'cancel-visit-btn';

const VisitsDataGrid: React.FC<Props> = ({ visits, isLoading, onCancelVisit, showDetails }) => {
  const { translation } = useTranslation();
  const { openModal } = useModalContext();
  const headerWithId = renderHeaderWithId();

  const rows = useMemo(() => visits.map((visit) => ({ id: visit.id, visit })), [visits]);

  const handleOpenCancelModal = (visit: QrCodeVisitWeb) => {
    if (!onCancelVisit) return;
    openModal(<CancelVisitModal visitorName={visit.name} onConfirm={() => onCancelVisit(visit.id)} />);
  };

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      field: 'dateTime',
      headerName: translation.date_time,
      type: 'dateTime',
      flex: 1,
      renderHeader: headerWithId,
      valueGetter: (_, row) => new Date(row.visit.datetime),
      renderCell: (params) => (
        <TwoLineCell
          title={formatDate(params.row.visit.datetime, 'dateOnly')}
          content={formatDate(params.row.visit.datetime, 'timeOnly')}
        />
      )
    },
    {
      field: 'visitor',
      headerName: translation.visitor,
      sortable: false,
      flex: 1,
      renderHeader: headerWithId,
      valueGetter: (_, row) => row.visit.name,
      renderCell: (params) => <TwoLineCell title={params.row.visit.name} content={params.row.visit.email} />
    },
    {
      field: 'host',
      headerName: translation.host,
      sortable: false,
      flex: 1,
      renderHeader: headerWithId,
      valueGetter: (_, row) => row.visit.host.name,
      renderCell: (params) => <TwoLineCell title={params.row.visit.host.name} content={params.row.visit.host.email} />
    },
    ...(showDetails
      ? [
          {
            field: 'details',
            headerName: translation.details,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            flex: 1,
            renderHeader: headerWithId,
            renderCell: (params: { row: (typeof rows)[number] }) => {
              const { status } = params.row.visit;
              if (status === QrCodeVisitStatus.Used) return translation.qr_code_used;
              if (status === QrCodeVisitStatus.Valid) return translation.expired;
              return null;
            }
          }
        ]
      : []),
    ...(onCancelVisit
      ? [
          {
            field: 'actions',
            headerName: '',
            minWidth: 160,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            resizable: false,
            align: 'right' as const,
            headerAlign: 'right' as const,
            renderHeader: headerWithId,
            renderCell: (params: { row: (typeof rows)[number] }) => (
              <Box pr={1}>
                <Button
                  className={CANCEL_BUTTON_CLASS}
                  variant="text"
                  color="error"
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleOpenCancelModal(params.row.visit);
                  }}
                >
                  {translation.cancel_visit}
                </Button>
              </Box>
            )
          }
        ]
      : [])
  ];

  return (
    <DataGrid
      className="not-clickable"
      rows={rows}
      columns={columns}
      autoHeight
      rowHeight={DATAGRID_ROW_HEIGHT}
      rowSelection={false}
      loading={isLoading}
      hideFooter={rows.length <= DATAGRID_DEFAULT_PAGE_SIZE}
      slotProps={{
        loadingOverlay: {
          variant: 'skeleton',
          noRowsVariant: 'skeleton'
        }
      }}
      disableRowSelectionOnClick
      disableColumnSelector
      disableColumnFilter
      disableColumnMenu
      slots={dataGridIdSlots}
      pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: DATAGRID_DEFAULT_PAGE_SIZE
          }
        }
      }}
      sx={{
        [`& .${CANCEL_BUTTON_CLASS}`]: {
          opacity: 0,
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          transition: 'opacity 0.2s ease-in-out'
        },
        [`& .MuiDataGrid-row:hover .${CANCEL_BUTTON_CLASS}`]: {
          opacity: 1,
          pointerEvents: 'auto'
        },
        '@media (hover: none), (pointer: coarse)': {
          [`& .${CANCEL_BUTTON_CLASS}`]: {
            opacity: 1,
            pointerEvents: 'auto'
          }
        }
      }}
    />
  );
};

export default VisitsDataGrid;
